import React, { useEffect, useMemo, useState } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import { Abc, GraphicEq, Image, Link } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { trackPromise } from 'react-promise-tracker'
import { useParams } from 'react-router-dom'
import { type MRT_ColumnDef } from 'material-react-table'
import api from '../../../api'
import { convertToLabelValue, fixedMonths, isSuccess, showError } from '../../../utils/helper'
import CustomTable from '../../../components/UI/CustomTable'
import HandleExport from '../../../components/UI/HandleExport'
import ImagesCarousel from '../../../components/UI/ImagesCarousel'
import DateFilters from '../../../components/UI/DateFilters'
import TotalCountCard from '../TotalCountCard'
import ListView from '../../../components/UI/ListView'
import CustomBarChart from '../../../components/UI/Charts/CustomBarChart'
import QueryByTypeChart from '../../ProjectManagement/NaiHub/NaiHubDashboard/QueryByTypeChart'
import ExpandableText from '../../../components/UI/ExpandableText'

const NaiHubDashboard = () => {
  const { id } = useParams() as any
  const [handleForceUpdate, setHandleForceUpdate] = useState<boolean>(false)
  const [loader, setLoader] = useState(false)
  const [fromDate, setFromDate] = useState<any>(null)
  const [toDate, setToDate] = useState<any>(null)
  const [sourceType, setSourceType] = useState('whatsapp') as any
  const [data, setData] = useState() as any
  const [videoAnalyticsData, setVideoAnalyticsData] = useState() as any
  const [userAnalyticsData, setUserAnalyticsData] = useState() as any
  const [totalParticipants, setTotalParticipants] = useState(0) as any
  const [selectedProject, setSelectedProject] = useState([]) as any
  const [selectedLanguage, setSelectedLanguage] = useState([]) as any
  const [selectedTopic, setSelectedTopic] = useState([]) as any
  const [selectedLink, setSelectedLink] = useState([]) as any

  const [chartData, setChartData] = useState({
    keys: [],
    values: [],
  }) as any

  useEffect(() => {
    getDashboard()
    getProjects({
      pagination: {
        pageIndex: 1,
        pageSize: 100,
      },
    })
    getVideoAnalytics({
      pagination: {
        pageIndex: 1,
        pageSize: 100,
      },
    })
  }, [sourceType, fromDate, toDate])

  const getDashboard = () => {
    trackPromise(
      api
        .getNaiHubDashboardCharts(sourceType, id, fromDate, toDate)
        .then((response: any) => {
          if (isSuccess(response)) {
            setChartData(response?.data?.data)
          }
        })
        .catch((error: any) => {
          showError(error)
        }),
    )
  }

  const getProjects = (p?: any) => {
    setLoader(true)
    api
      .getNaiHubDashboardQuery(sourceType, id, p?.pagination, fromDate, toDate)
      .then((response: any) => {
        setLoader(false)
        if (isSuccess(response)) {
          setData(response?.data)
        }
      })
      .catch((error: any) => {
        setLoader(false)
        showError(error)
      })
  }
  const getVideoAnalytics = (p?: any) => {
    setLoader(true)
    api
      .getNaiHubVideoAnalytics(sourceType, id, p?.pagination, fromDate, toDate)
      .then((response: any) => {
        setLoader(false)
        if (isSuccess(response)) {
          let video_analytics = response?.data?.data.data?.video_analytics || [];
          let user_analytics = response?.data?.data.data?.user_analytics || [];
          let total_participants = response?.data?.data.data?.total_participants || 0;
          setVideoAnalyticsData(video_analytics)
          setUserAnalyticsData(user_analytics)
          setTotalParticipants(total_participants)
        }
      })
      .catch((error: any) => {
        setLoader(false)
        showError(error)
      })
  }

  type User = {
    query: string
    response: string
    timestamp: string
    sources?: any
    email?: any
    uploaded_document?: any
  }

  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: 'query',
        minSize: 80,
        header: 'Query',
        Cell: ({ cell }) => <Box component="span">{cell.getValue<string>() || '-'}</Box>,
      },
      {
        accessorKey: 'response',
        minSize: 400,
        maxSize: 600,
        header: 'Response',
        Cell: ({ cell }) => <ExpandableText text={cell.getValue<string>() || '-'} limit={100} />,
      },
      {
        accessorKey: 'sources',
        header: 'Sources',
        maxSize: 600,
        Cell: ({ cell }) => (
          <Box component="span">
            {cell.getValue<any>()?.length
              ? cell.getValue<any>()?.map((sours: any, ind: number) => (
                  <Box key={ind}>
                    <a href={sours?.url} target="_blank" className="text-blue-500">
                      <Link className="mr-1 text-gray-400" />
                      {sours?.name}
                    </a>
                  </Box>
                ))
              : 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'input_type',
        header: 'Input Type',
        maxSize: 160,
        Cell: ({ cell }) => (
          <Box className="flex flex-col items-center w-full -ml-4">
            <div>
              {cell.getValue<string>() === 'audio' ? (
                <GraphicEq />
              ) : cell.getValue<string>() === 'image' ? (
                <Image fontSize="small" />
              ) : cell.getValue<string>() === 'text' ? (
                <Abc />
              ) : (
                ''
              )}
            </div>
            <Typography fontSize={'small'}>{cell.getValue<string>() || 'N/A'}</Typography>
          </Box>
        ),
      },
      {
        accessorKey: 'uploaded_document',
        header: 'Uploaded Document',
        maxSize: 160,
        Cell: ({ row }) => (
          <React.Fragment>
            {row?.original?.uploaded_document?.image_url ? (
              <ImagesCarousel
                showThumbnail={true}
                row={row?.original}
                images={row?.original?.uploaded_document?.image_url ? [row?.original?.uploaded_document?.image_url] : []}
                thumbnails={row?.original?.uploaded_document?.thumbnail_url ? [row?.original?.uploaded_document?.thumbnail_url] : []}
              />
            ) : (
              'N/A'
            )}
          </React.Fragment>
        ),
      },
      {
        accessorKey: 'timestamp',
        minSize: 180,
        header: 'Timestamp',
        Cell: ({ cell }) => (
          <Box component="span" className="capitalize">
            {cell.getValue<string>() || 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'timezone',
        minSize: 160,
        header: 'Timezone',
        Cell: ({ cell }) => (
          <Box component="span" className="capitalize">
            {cell.getValue<string>() || 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email Address',
        Cell: ({ cell }) => <Box component="span">{cell.getValue<string>() || 'N/A'}</Box>,
      },
    ],
    [sourceType],
  )

  const applyDateFilter = (f: any, t: any) => {
    setFromDate(f)
    setToDate(t)
  }

  const handleReset = () => {
    setFromDate(null)
    setToDate(null)
    setSourceType('')
  }

  const handleSourceTypeChange = (value: string) => {
    setSourceType(value)
    setHandleForceUpdate(!handleForceUpdate)
  }

  const handleDateRangeChange = (from: Date | null, to: Date | null) => {
    setFromDate(from)
    setToDate(to)
    setHandleForceUpdate(!handleForceUpdate)
  }

  const modifiedChartResponse = useMemo(() => {
    const charts = {
      labels: [],
      values: [],
    } as any

    chartData?.recorded_queries?.forEach((c: any) => {
      charts.labels.push(`${fixedMonths[parseInt(c?.month) - 1]} ${c?.year}`)
      charts.values.push(c?.query_count || 0)
    })

    return charts
  }, [chartData, chartData?.recorded_queries, chartData?.recorded_queries?.length])

  const transformedUserAnalytics = useMemo(() => {
    if (!userAnalyticsData || !videoAnalyticsData) return []
    
    const topics = videoAnalyticsData?.map((v: any) => v.video_topic) || []
    const userMap = new Map()
    
    // Group user analytics by user_name
    userAnalyticsData.forEach((user: any) => {
      const userName = user.user_name
      if (!userMap.has(userName)) {
        userMap.set(userName, { user_name: userName })
      }
      // Add first_viewed_at under the corresponding topic
      userMap.get(userName)[user.video_topic] = user.first_viewed_at
    })
    
    // Convert map to array and ensure all topics are present
    const result = Array.from(userMap.values())
    result.forEach((user: any) => {
      topics.forEach((topic: string) => {
        if (!user[topic]) {
          user[topic] = '-'
        }
      })
    })
    
    return result
  }, [userAnalyticsData, videoAnalyticsData])

  return (
    <React.Fragment>
      <Helmet>
        <title>Dashboard</title>
      </Helmet>
      <Box className="p-2 md:p-4">
        <DateFilters
          platformOptions={[
            { label: 'WhatsApp', value: 'whatsapp' },
            { label: 'nAI', value: 'nai' },
          ]}
          fromDate={fromDate}
          toDate={toDate}
          onDateRangeChange={handleDateRangeChange}
          applyDateFilter={applyDateFilter}
          sourceType={sourceType}
          onSourceTypeChange={handleSourceTypeChange}
          onReset={handleReset}
          chipTitle={'Observations'}
          showPlatformFilter={true}
        />

        <Box sx={{ bgcolor: 'secondary.main' }} className="py-4 px-2 rounded-lg relative">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6} lg={3} xl={2.4}>
                  <TotalCountCard totalCount={chartData?.total_users || 0} title="Total Users" />
                </Grid>
                <Grid item xs={6} lg={3} xl={2.4}>
                  <TotalCountCard totalCount={chartData?.total_query || 0} title="Total Queries" />
                </Grid>
                <Grid item xs={6} lg={3} xl={2.4}>
                  <TotalCountCard totalCount={chartData?.total_link_shared || 0} title="Total Links Shared" />
                </Grid>
                <Grid item xs={6} lg={3} xl={2.4}>
                  <TotalCountCard totalCount={chartData?.daily_active_users || 0} title="Daily Active Users (DAU)" />
                </Grid>
                <Grid item xs={6} lg={3} xl={2.4}>
                  <TotalCountCard totalCount={chartData?.monthly_active_users || 0} title="Monthly Active Users (MAU)" />
                </Grid>
                {chartData?.is_median_response_time_visible ? (
                  <Grid item xs={6} lg={3} xl={2.4}>
                    <TotalCountCard totalCount={chartData?.median_response_time || 0} title="Median Response Time" />
                  </Grid>
                ) : null}
                <Grid item xs={6} lg={3} xl={2.4}>
                  <TotalCountCard totalCount={totalParticipants || 0} title="Total Quiz Participants" />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                  <ListView
                    blockAction={true}
                    filters={selectedProject}
                    handleFilterChange={setSelectedProject}
                    bodyClassName="sm:h-[238px] overflow-auto custom-scrollbar"
                    labelTitle="Project"
                    valueTitle="Queries Count"
                    data={chartData?.project_wise_count ? convertToLabelValue(chartData?.project_wise_count) : []}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={3}>
                  <ListView
                    blockAction={true}
                    filters={selectedLanguage}
                    handleFilterChange={setSelectedLanguage}
                    bodyClassName="sm:h-[238px] overflow-auto custom-scrollbar"
                    labelTitle="Language"
                    valueTitle="Queries Count"
                    data={chartData?.query_language_count ? convertToLabelValue(chartData?.query_language_count, 'language', 'query_count') : []}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={3}>
                  <ListView
                    blockAction={true}
                    filters={selectedTopic}
                    handleFilterChange={setSelectedTopic}
                    bodyClassName="sm:h-[238px] overflow-auto custom-scrollbar"
                    labelTitle="Topics Queried"
                    valueTitle="Count"
                    data={chartData?.topic_list_with_count ? convertToLabelValue(chartData?.topic_list_with_count, 'topic_name', 'count') : []}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={3}>
                  <ListView
                    blockAction={true}
                    filters={selectedLink}
                    handleFilterChange={setSelectedLink}
                    bodyClassName="sm:h-[238px] overflow-auto custom-scrollbar"
                    labelTitle="Most shared Links"
                    valueTitle="Count"
                    data={
                      chartData?.most_shared_links
                        ? chartData?.most_shared_links?.map((links: any) => {
                            return {
                              ...links,
                              label: (
                                <a href={links?.link} target="__blank" className="text-blue-500">
                                  {links?.link}
                                </a>
                              ),
                              value: links?.count,
                            }
                          })
                        : []
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <QueryByTypeChart query_by_type={chartData?.query_by_type} />
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                  <Box className={'bg-white rounded-lg min-h-[382px] flex items-center justify-center'}>
                    <CustomBarChart
                      height={'373'}
                      fromDate={fromDate}
                      toDate={toDate}
                      title={'Recorded Queries'}
                      xLabelsProp={modifiedChartResponse?.labels}
                      data={modifiedChartResponse?.values}
                      className={'p-2'}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Grid item xs={12} className="p-2 md:p-4">
        <Typography className="!font-semibold mt-2">Video Analytics</Typography>
        <div className="w-full relative !bg-transparent mb-8">
          <CustomTable
            loader={loader}
            columns={[
              {
                accessorKey: 'video_topic',
                header: 'Video Topic',
              },
              {
                accessorKey: 'unique_users',
                header: 'Unique Users',
              },
              {
                accessorKey: 'total_views',
                header: 'Total Views',
              },
            ]}
            data={videoAnalyticsData || []}
            rowCount={videoAnalyticsData?.length || 0}
            getApiCall={getVideoAnalytics}
            // prefix={<HandleExport APIKey="video_analytics" fromDate={fromDate} toDate={toDate} platform={sourceType} />}
            manualPagination={true}
          />
        </div>
        <Typography className="!font-semibold mt-6">User Analytics</Typography>
        <div className="w-full relative !bg-transparent">
          <CustomTable
            loader={loader}
            columns={
              (() => {
                const topics = videoAnalyticsData?.map((v: any) => v.video_topic) || [];
                return [
                  {
                    accessorKey: 'user_name',
                    header: 'Username',
                    Cell: ({ cell }: any) => cell.getValue() || '-',
                  },
                  ...topics.map((topic: any) => ({
                    accessorKey: topic,
                    header: topic,
                    Cell: ({ cell }: any) => cell.getValue() || '-',
                  })),
                ];
              })()
            }
            data={transformedUserAnalytics || []}
            rowCount={transformedUserAnalytics?.length || 0}
            // No export for this one, but you can add if needed.
            manualPagination={false}
          />
        </div>
      </Grid>
      <Grid item xs={12} className="p-2 md:p-4">
        <Typography className="!font-semibold mt-2">Recent Queries</Typography>
        <div className="w-full relative !bg-transparent">
          <CustomTable
            loader={loader}
            columns={columns}
            rowCount={data?.meta?.total_count}
            data={data?.data || []}
            columnPayload={data?.data?.column_selection || []}
            getApiCall={getProjects}
            prefix={<HandleExport APIKey="recent-queries" fromDate={fromDate} toDate={toDate} platform={sourceType} />}
            manualPagination={true}
          />
        </div>
      </Grid>
    </React.Fragment>
  )
}

export default NaiHubDashboard
